import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { promptCrazyGamesLogin } from '../services/crazygamesAuth';
import { PlayerRole } from '../types';

export default function Navbar() {
  const { isAuthenticated, role, logout, authSource, userId } = useAuthStore();
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  // A CrazyGames guest can upgrade to their CrazyGames account (progress migrates).
  const isCrazyGuest = authSource === 'crazygames' && !!userId?.startsWith('cgguest-');

  // Not signed in → the only reachable page is the login/home screen (all game
  // routes redirect there), so a nav bar with redundant "Home"/"Login" links
  // pointing at the same page adds nothing. Hide it; the hero shows the branding.
  if (!isAuthenticated) return null;

  return (
    <nav className="navbar">
      <Link
        to="/"
        className="navbar-brand"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}
      >
        <img src="/logo.svg" alt="ROOTKIN" width={28} height={28} style={{ display: 'block' }} />
        <span className="navbar-brand-text" style={{ letterSpacing: '0.18em' }}>ROOTKIN</span>
      </Link>

      <ul className="navbar-nav">
        <li>
          <Link to="/" style={{ color: isActive('/') ? 'var(--primary-color)' : undefined }}>
            Home
          </Link>
        </li>
        <li>
          <Link to="/missions" style={{ color: isActive('/missions') ? 'var(--primary-color)' : undefined }}>
            Missions
          </Link>
        </li>
        <li>
          <Link to="/store" style={{ color: isActive('/store') ? 'var(--primary-color)' : undefined }}>
            Store
          </Link>
        </li>
        <li>
          <Link to="/leaderboard" style={{ color: isActive('/leaderboard') ? 'var(--primary-color)' : undefined }}>
            Leaderboard
          </Link>
        </li>
        <li>
          <Link
            to="/notifications"
            style={{
              color: isActive('/notifications') ? 'var(--primary-color)' : undefined,
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
            }}
          >
            Notifications
            {unreadCount > 0 && (
              <span style={{
                background: '#ef4444',
                color: '#fff',
                borderRadius: '999px',
                fontSize: '0.62rem',
                fontWeight: 'bold',
                padding: '0 0.35rem',
                lineHeight: '1.5',
                minWidth: '1.1rem',
                textAlign: 'center',
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>
        </li>
        <li>
          <Link to="/hacked-nodes" style={{ color: isActive('/hacked-nodes') ? 'var(--primary-color)' : undefined }}>
            Hacked Nodes
          </Link>
        </li>
        <li>
          <Link to="/cooldowns" style={{ color: isActive('/cooldowns') ? 'var(--primary-color)' : undefined }}>
            Targets
          </Link>
        </li>
        <li>
          <Link to="/profile" style={{ color: isActive('/profile') ? 'var(--primary-color)' : undefined }}>
            Profile
          </Link>
        </li>
        {(role === PlayerRole.Admin || role === PlayerRole.Moderator) && (
          <li>
            <Link
              to="/admin"
              style={{ color: isActive('/admin') ? 'var(--primary-color)' : undefined, fontWeight: 'bold' }}
            >
              {role === PlayerRole.Admin ? '[ADMIN]' : '[MOD]'}
            </Link>
          </li>
        )}
        {/* CrazyGames guests can log in to their CrazyGames account (progress migrates). */}
        {isCrazyGuest && (
          <li>
            <button
              onClick={() => { void promptCrazyGamesLogin(); }}
              style={{ padding: '0.3em 0.8em', fontSize: '0.9em', fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              Log in with CrazyGames
            </button>
          </li>
        )}
        {/* Logout is shown for the username ('dev') login. */}
        {authSource === 'dev' && (
          <li>
            <button onClick={() => logout()} style={{ padding: '0.3em 0.8em', fontSize: '0.9em' }}>
              Logout
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
}
